import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';

import * as fromHeader from '../../../store';
import * as fromGlobalCart from '~shared/modules/shopping-cart-global/store';
import {
  APP_USER_VALIDATE_EMAIL_ROUTE,
  BUY_AGAIN,
  EXPERIMENT_VARIATIONS,
  FeatureToggleEnum,
  MULTIPLE_SHOPPING_LIST,
  NUMBER_2000,
  NUMBER_500,
  NUMBER_7000,
  SHOPPING_LIST_V2,
  APPROVAL_DASHBOARD,
  ORDER_HISTORY_LINK_EXTERNAL,
  BUY_AGAIN_EXTERNAL,
} from '~shared/constants';
import { MobileMenuService } from '~app/modules/generic/services/mobile-menu.service';
import { SECONDARY_HEADER_HTML_ID } from '~app/modules/generic/constants';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import {
  GaTrackingEventAction,
  GaTrackingEventCategory,
  GaTrackingEventLabel,
  GaTrackingEventName,
  GATrackingEventType,
} from '~shared/modules/ga-tracking/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { PageDetectionUtil } from '~shared/utils/page-detection.util';
import { CartLinkClickApplicationEvent } from '~shared/modules/application-event/models';
import { SECONDARY_HEADER_ANIMATIONS } from './animations/secondary-header.animations';
import { CartState, selectShoppingCartData } from '~shared/modules/shopping-cart-store';
import { MiniCartApplicationEvent } from '~shared/modules/application-event/models/mini-cart-application-event.model';
import { ApplicationMiniCartEventTypeEnum } from '~shared/modules/application-event/constants';

import { CachedCategoriesUtil } from '~app/modules/generic/utils/cached-categories.util';
import { isPlatformBrowser } from '@angular/common';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { CategoryNavigationService } from '~app/modules/generic/services/category-navigation.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { Observable } from 'rxjs/internal/Observable';
import { RegionSelectorService } from '~shared/modules/region-selector/services';
import { IOfferLine } from '~shared/modules/cart/models';
import { mapOfferItemAttributes } from '~app/modules/generic/utils/mapOfferItemAttributes';
import { AccountTypeInfo, IAccountInformation } from '~shared/models';
import { environment } from '~env/environment';
import { toggleFlyoutEventKey } from '~server/utils/auth.utils';
import { IdentityService } from '~app/core/modules/identity/services/identity.service';
import { IS_COMPANION } from '~app/app.module';
import { SuggestionsPopoverService } from '~app/modules/generic/services/suggestions-popover.service';
import { EMPTY_CART_DATA, ShoppingCartData } from '~shared/modules/shopping-cart-store/models';

@Component({
  selector: 'app-secondary-header',
  templateUrl: './secondary-header.component.html',
  styleUrls: ['./secondary-header.component.scss'],
  animations: SECONDARY_HEADER_ANIMATIONS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecondaryHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isDesktop: boolean;
  @Input() isSearchOnFocus: boolean;
  @Input() user: IAccountInformation;
  @ViewChild('shoppingCartIcon') shoppingCartIcon: ElementRef<HTMLElement>;
  @ViewChild('secondaryHeader') secondaryHeader: ElementRef;

  readonly SECONDARY_HEADER_ID = SECONDARY_HEADER_HTML_ID;

  isMenuOpen$ = this.mobileMenuService.isMenuOpen$;
  destroySubject$ = new Subject<void>();
  shoppingCartData$ = new BehaviorSubject<ShoppingCartData>(EMPTY_CART_DATA);
  showMiniCartTimer;
  isHover = false;
  mobileMenuShown = false;
  isNavigationuBarEnabledV2: boolean;
  isAccountSubsidiary: boolean;
  isAccountSubsidiary$: Observable<boolean>;
  shoppingListUrl = `${environment.appBaseUrl()}${SHOPPING_LIST_V2}`;
  public readonly buyAgainLink = BUY_AGAIN;
  public readonly orderHistoryLinkExternal = ORDER_HISTORY_LINK_EXTERNAL();
  public readonly buyAgainExternalLink = BUY_AGAIN_EXTERNAL();
  public readonly approvalDashboardLink = `${environment.appBaseUrl()}${APPROVAL_DASHBOARD}`;
  showApprovalDashboard: boolean;
  isShowingOrdersHistoryLink$ = this.featureFlagService.isFeatureEnabledAndReadyV2(
    FeatureToggleEnum.ODR_TMP_1722_HEADER_SHOW_ORDERS_INSTEAD_OF_BUY_AGAIN
  );
  multipleShoppingListUrl = `${environment.appBaseUrl()}${MULTIPLE_SHOPPING_LIST}`;
  regionSelector$: Observable<boolean> = this.regionSelectorService.getActiveRegionSelector();
  // @TODO: Remove FF logic whenever is not needed (MACO-1470)
  public totalCartQuantity$ = this.store.pipe(select(fromGlobalCart.getCartTotalQuantity));
  public showMiniCart$ = this.store.pipe(select(fromGlobalCart.showMiniCart));
  // @TODO: Remove Optimizely logic whenever is not needed (MACO-1485)
  private isSidenavMenuFFEnabled: boolean = false;
  private shouldShowSidenavBasedOnVariation: boolean = false;

  constructor(
    private store: Store<fromHeader.BuyerState>,
    private store$: Store<CartState>,
    private mobileMenuService: MobileMenuService,
    private applicationEventService: ApplicationEventService,
    private router: Router,
    private mediaObserver: MediaObserver,
    private categoryNavigationService: CategoryNavigationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly featureFlagService: ActiveFeaturesConfigcatService,
    private readonly accountInfoService: AccountInfoService,
    private gtmDataLayerService: GtmDatalayerService,
    private readonly regionSelectorService: RegionSelectorService,
    private readonly identityService: IdentityService,
    @Optional() @Inject(IS_COMPANION) public isCompanion,
    private suggestionsPopoverService: SuggestionsPopoverService
  ) {}

  get isSidenavMenuVisible(): boolean {
    return this.isSidenavMenuFFEnabled && this.shouldShowSidenavBasedOnVariation;
  }

  ngOnInit() {
    this.isNewMenuBarEnabled();
    this.accountInfoService
      .getAccountInfo()
      .pipe(
        takeUntil(this.destroySubject$),
        filter((accountInfo) => accountInfo !== null)
      )
      .subscribe((accountInfo) => {
        this.isAccountSubsidiary = accountInfo?.isSubsidiary;
      });

    this.accountInfoService
      .getAccountInfoV2()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((accountInfo) => {
        const isHeadQuarter = accountInfo?.customer?.accountType == AccountTypeInfo.HEAD_QUARTER;
        this.showApprovalDashboard = isHeadQuarter && accountInfo?.scopes?.includes('checkout:manage');
      });

    this.isAccountSubsidiary$ = this.accountInfoService.isSubsidiaryAccount();

    combineLatest([this.store$.pipe(select(selectShoppingCartData), takeUntil(this.destroySubject$))]).subscribe(
      ([cartData]) => {
        cartData = {
          ...cartData,
          offers: cartData.offers.map((offer: IOfferLine) => {
            return mapOfferItemAttributes(offer);
          }),
        };
        this.shoppingCartData$.next(cartData);
        if (!this.isHover) {
          this.closeMiniCart(NUMBER_7000);
        }
      }
    );

    this.loadSidenavMenuEnablers();
  }

  ngAfterViewInit() {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        if (this.mediaObserver.isActive(MEDIA_ALISES.mobile)) {
          this.suggestionsPopoverService.initHeaderSearch(this.secondaryHeader.nativeElement);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  checkRoutesEnabledForMiniCart() {
    const pageUrl = this.router.url;
    return !PageDetectionUtil.isCheckoutPage(pageUrl);
  }

  checkIfMobileOrTablet() {
    return this.mediaObserver.isActive(MEDIA_ALISES.mobile) || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
  }

  onMouseHover(e) {
    clearTimeout(this.showMiniCartTimer);
    this.isHover = true;
    if (this.checkRoutesEnabledForMiniCart() && !this.checkIfMobileOrTablet()) {
      this.store.dispatch(new fromGlobalCart.ShowMiniCart());
    }
    this.trackMiniCartHover();
  }

  onMouseLeave(e) {
    this.isHover = false;
    this.closeMiniCart(NUMBER_500);
  }

  onShowMiniCart(e) {
    if (!e.value) {
      this.isHover = false;
      clearTimeout(this.showMiniCartTimer);
      this.closeMiniCart(e.timer);
    } else {
      this.isHover = true;
      clearTimeout(this.showMiniCartTimer);
    }
  }

  closeMiniCart(timer: number) {
    if (isPlatformBrowser(this.platformId)) {
      this.showMiniCartTimer = setTimeout(() => {
        this.store.dispatch(new fromGlobalCart.CloseMiniCart());
      }, timer);
    }
  }

  public isNewMenuBarEnabled(): void {
    this.featureFlagService
      .isFeatureEnabledAndReadyV2(FeatureToggleEnum.FF_BOE_SHOPPING_LIST)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((isEnabled) => {
        this.isNavigationuBarEnabledV2 = isEnabled;
      });
  }
  public onShoppingListClick(event, multipleShoppingList?: boolean): void {
    this.sendGAEventShoppingList();

    if (!this.user) {
      event.preventDefault();
      this.redirectToValidateEmailPage(multipleShoppingList ? MULTIPLE_SHOPPING_LIST : SHOPPING_LIST_V2);
    }
  }
  private sendGAEventShoppingList(): void {
    this.gtmDataLayerService.gtagEvent({
      category: GaTrackingEventCategory.ShoppingListV2,
      action: GaTrackingEventAction.ShoppingListHeader,
      event: GaTrackingEventName.shoppingListHeaderButtonInteraction,
      GA_eventname: GaTrackingEventName.Interaction,
      label: `null`,
    });
  }

  public onBuyAgainLinkClick(event): void {
    this.sendGAEventBuyAgain();
    if (!this.user) {
      event.preventDefault();
      this.redirectToValidateEmailPage(BUY_AGAIN);
    }
  }

  public onOrdersHistoryLinkClick(event): void {
    this.sendGAEventOrdershistory();
  }

  public onApprovalDashboardLinkClick(): void {
    this.sendGAEventApprovalDashboard();
  }

  private sendGAEventBuyAgain(): void {
    this.gtmDataLayerService.gtagEvent({
      category: GaTrackingEventCategory.Interaction,
      action: GaTrackingEventAction.BuyAgainHeader,
      event: GaTrackingEventName.BuyAgainHeaderInteraction,
      label: `null`,
    });
  }

  private sendGAEventApprovalDashboard(): void {
    this.gtmDataLayerService.gtagEvent({
      category: GaTrackingEventCategory.Interaction,
      action: GaTrackingEventAction.ApprovalDashboardHeader,
      event: GaTrackingEventName.ApprovalsHeaderLinkClick,
      label: `null`,
    });
  }

  private sendGAEventOrdershistory(): void {
    this.gtmDataLayerService.gtagEvent({
      event: GATrackingEventType.OrdersHistoryHeaderLinkClick,
      GA_eventname: GaTrackingEventName.Interaction,
      action: GaTrackingEventAction.OrdersHistoryHeaderLinkClick,
      category: GaTrackingEventCategory.OrdersHistory,
      label: null,
    });
  }

  onClickGoToCart() {
    this.trackCartLogoClick();
  }

  trackCartLogoClick() {
    const pageUrl = this.router.url;
    let pageName: string;
    if (PageDetectionUtil.isHomePage(pageUrl)) {
      pageName = GaTrackingEventLabel.HomePage;
    } else if (PageDetectionUtil.isProductListPage(pageUrl)) {
      pageName = GaTrackingEventLabel.CategoryPage;
    } else if (PageDetectionUtil.isProductDetailsPage(pageUrl)) {
      pageName = GaTrackingEventLabel.ProductPage;
    } else {
      pageName = GaTrackingEventLabel.CartPage;
    }
    this.applicationEventService.publish(new CartLinkClickApplicationEvent(pageName));
  }

  showSidenavMenu(): void {
    this.categoryNavigationService.toggleSidenav();
  }

  showMobileMenu(origin) {
    this.mobileMenuService.show(origin);
    this.fireSidenavEvent('Open');
  }

  hideMobileMenu() {
    if (!CachedCategoriesUtil.isCachedCategoriesExisted()) {
      this.store.dispatch(new fromHeader.LoadCategories());
    }

    this.mobileMenuService.close();
    this.fireSidenavEvent('Close');
  }

  trackMiniCartHover() {
    setTimeout(() => {
      if (this.isHover) {
        this.applicationEventService.publish(
          new MiniCartApplicationEvent(ApplicationMiniCartEventTypeEnum.MiniCartView, GaTrackingEventLabel.HoveringOver)
        );
      }
    }, NUMBER_2000);
  }

  toggleFlyout(event) {
    event.preventDefault();
    this.identityService
      .isLoggedIn()
      .pipe(
        tap((isLoggedIn) => {
          if (isLoggedIn) {
            const toggleFlyoutEvent = new CustomEvent(toggleFlyoutEventKey);
            window.dispatchEvent(toggleFlyoutEvent);
          } else {
            this.redirectToValidateEmailPage(this.router.url.split('?')[0]);
          }
        })
      )
      .subscribe();
  }
  private loadSidenavMenuEnablers(): void {
    this.categoryNavigationService
      .isSidenavMenuEnabled()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(([isEnabled, variation]) => {
        this.isSidenavMenuFFEnabled = isEnabled;
        this.shouldShowSidenavBasedOnVariation = !variation || variation === EXPERIMENT_VARIATIONS.VARIANT_TWO;
      });
  }

  private fireSidenavEvent(state: string): void {
    this.gtmDataLayerService.gtagEvent({
      category: GaTrackingEventCategory.Navigation,
      action: GaTrackingEventAction.NavigationClick,
      event: GATrackingEventType.Navigation,
      label: `${state} Mobile Menu`,
    });
  }

  private redirectToValidateEmailPage(url: string) {
    this.router.navigate([APP_USER_VALIDATE_EMAIL_ROUTE], {
      queryParams: { backUrl: url },
    });
  }
}
